import React from "react"
import { useTranslation } from "react-i18next"

import { IconSize } from "shared/types/Icon"
import { ButtonVariant } from "shared/ui/buttons/Button"
import { Hint } from "shared/ui/hint/Hint"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { MarkdownPreview } from "shared/ui/input/markdown/components/MarkdownPreview"
import { FormOverlay } from "shared/ui/overlay/FormOverlay"

import { TeamRole } from "entities/team-members/types/TeamRole"

import "../styles/_team-leave-overlay.scss"
import { AdminTeamLeaveStepIndicator } from "./AdminTeamLeaveStepIndicator"

export interface DefaultTeamLeaveOverlayViewProps {
  role: TeamRole | null | undefined
  isLastAdmin: boolean
  isLastTeamMember: boolean
  isUpdatingAdmin?: boolean
  wasLastAdmin?: boolean
  onSubmit: () => void
  onCancel: () => void
}

export const DefaultTeamLeaveOverlayView = React.memo(
  ({
    role,
    isLastAdmin,
    isLastTeamMember,
    wasLastAdmin,
    isUpdatingAdmin,
    onSubmit,
    onCancel
  }: DefaultTeamLeaveOverlayViewProps) => {
    const { t } = useTranslation()

    return (
      <FormOverlay
        id="team-leave-overlay"
        form="team-leave-overlay"
        className="team-leave-overlay"
        headline={
          isLastTeamMember
            ? t("Team.Leave.CannotLeave")
            : t("Team.Leave.AreYouSure")
        }
        submitButtonText={t("Team.Actions.Leave")}
        cancelButtonText={t("General.Cancel")}
        submitButtonProps={{
          disabled: isUpdatingAdmin,
          isLoading: isUpdatingAdmin,
          hidden: isLastTeamMember,
          icon: IconName.LOGOUT_LEFT,
          className: "team-leave-actions__submit",
          onClick: onSubmit,
          isDanger: true
        }}
        cancelButtonProps={{
          variant: isLastTeamMember
            ? ButtonVariant.PRIMARY
            : ButtonVariant.TERTIARY,
          className: "team-leave-actions__cancel"
        }}
        showCloseButton={!isLastTeamMember}
        onCancel={onCancel}
        onSubmit={onSubmit}
      >
        <div className="team-leave-overlay__body">
          {wasLastAdmin && !isLastTeamMember ? (
            <AdminTeamLeaveStepIndicator wasLastAdmin={wasLastAdmin} />
          ) : null}

          {isLastTeamMember ? (
            <Hint.Root data-testid="team-leave-last-member-hint">
              <Hint.Icon name={IconName.INFO} size={IconSize.XS} />
              <Hint.Content>
                <Hint.Title>
                  {t("Team.Leave.LastTeamMember.WarningTitle")}
                </Hint.Title>
                <Hint.Text>
                  {t("Team.Leave.LastTeamMember.WarningText")}
                </Hint.Text>
              </Hint.Content>
            </Hint.Root>
          ) : null}

          {!isLastTeamMember && role === TeamRole.READ ? (
            <>
              <div className="team-leave-overlay__text">
                <MarkdownPreview value={t("Team.Leave.Viewer.WarningText")} />
              </div>

              <div className="team-leave-overlay__subtext">
                {t("Team.Leave.Viewer.SubText")}
              </div>
            </>
          ) : null}

          {!isLastTeamMember && role === TeamRole.WRITE ? (
            <>
              <div className="team-leave-overlay__text">
                <MarkdownPreview value={t("Team.Leave.Editor.WarningText")} />
              </div>
              <div className="team-leave-overlay__subtext">
                {t("Team.Leave.Editor.SubText")}
              </div>
            </>
          ) : null}

          {!isLastTeamMember && role === TeamRole.ADMIN && !isLastAdmin ? (
            <>
              <div className="team-leave-overlay__text">
                <MarkdownPreview value={t("Team.Leave.Admin.WarningText")} />
              </div>
              <div className="team-leave-overlay__subtext">
                {t("Team.Leave.Admin.SubText")}
              </div>
            </>
          ) : null}
        </div>
      </FormOverlay>
    )
  }
)
